import { PBK_ENDPOINT } from "@Common/constants";
import type { EncryptedPayloadBodyI } from "@Common/types/bo/applications_public/applications_public";
import type { DataProvider } from "react-admin";

async function encryptForServer(publicKeyPem: string, message: string) {
	// --- Import RSA public key ---
	const binaryDer = Uint8Array.from(atob(publicKeyPem), (c) => c.charCodeAt(0));
	const rsaKey = await window.crypto.subtle.importKey(
		"spki",
		binaryDer.buffer,
		{ name: "RSA-OAEP", hash: "SHA-256" },
		false,
		["encrypt"],
	);

	// --- Generate AES key ---
	const aesKey = await crypto.subtle.generateKey(
		{ name: "AES-GCM", length: 256 },
		true,
		["encrypt", "decrypt"],
	);

	// --- Encrypt message with AES-GCM ---
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const encoded = new TextEncoder().encode(message);
	const ciphertextWithTag = await crypto.subtle.encrypt(
		{ name: "AES-GCM", iv },
		aesKey,
		encoded,
	);

	// Extract authTag (last 16 bytes)
	const full = new Uint8Array(ciphertextWithTag);
	const authTag = full.slice(full.length - 16);
	const ciphertext = full.slice(0, full.length - 16);

	// --- Export AES key and encrypt it with RSA ---
	const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);
	const encryptedKey = await crypto.subtle.encrypt(
		{ name: "RSA-OAEP" },
		rsaKey,
		rawAesKey,
	);

	// --- Helper for Base64 encoding ---
	const b64 = (buf: ArrayBuffer | Uint8Array) => {
		const bytes = new Uint8Array(buf);
		let binary = "";
		for (let i = 0; i < bytes.length; i++) {
			binary += String.fromCharCode(bytes[i] as number);
		}
		return btoa(binary);
	};

	// --- Return transport-safe payload ---
	return {
		e: b64(encryptedKey),
		i: b64(iv),
		a: b64(authTag),
		c: b64(ciphertext),
	};
}

export const fetchPublicKey = async (): Promise<string> => {
	const pbkResponse = await fetch(PBK_ENDPOINT);
	const pbkData = await pbkResponse.json();
	return pbkData.pbk;
};

export const sendEncryptedData = async (
	dataProvider: DataProvider,
	resourceName: string,
	recordId: string,
	// biome-ignore lint/suspicious/noExplicitAny: expected here
	payload: any,
	publicKey: string,
) => {
	// We generate AES key, encrypt payload with it, and encrypt AES key with RSA key
	// We do this because RSA encryption cannot encrypt large payloads
	const payloadEncrypted: EncryptedPayloadBodyI = await encryptForServer(
		publicKey,
		JSON.stringify(payload),
	);

	await dataProvider.update(resourceName, {
		previousData: {},
		id: recordId,
		data: payloadEncrypted,
	});
};
